import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { B2BPolicyType, GroupType } from 'sl-sdk';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ExternalDashboardLink from 'components/ExternalDashboardLink';

export type GroupContext = {
  group: GroupType;
  policies: B2BPolicyType[];
  policiesLoadFailed: boolean;
};

interface Props {
  readonly groupContexts: GroupContext[];
  readonly hasUserAbove: boolean;
}

const GroupsSection: React.FC<Props> = ({ groupContexts, hasUserAbove }) => {
  const intl = useIntl();

  if (groupContexts.length === 0) {
    return null;
  }

  const describeGroup = (group: GroupType): string =>
    [
      group.is_partner ? intl.formatMessage({ id: 'zendesk.sidebar.group.partner', defaultMessage: 'Partner' }) : null,
      group.siret || null,
      group.email || null,
    ]
      .filter(Boolean)
      .join(' · ');

  const renderPolicies = (policies: B2BPolicyType[], policiesLoadFailed: boolean): React.ReactNode => {
    if (policiesLoadFailed) {
      return (
        <Typography color="warning.main" variant="body2">
          <FormattedMessage
            defaultMessage="Could not load B2B policies"
            id="zendesk.sidebar.group.policiesLoadFailed"
          />
        </Typography>
      );
    }
    if (policies.length === 0) {
      return (
        <Typography color="text.secondary" variant="body2">
          <FormattedMessage defaultMessage="No B2B policies" id="zendesk.sidebar.group.noPolicies" />
        </Typography>
      );
    }
    return (
      <List dense disablePadding sx={{ pl: 1.5, width: '100%' }}>
        {policies.map(policy => (
          <ListItem key={policy.id} disableGutters>
            <ListItemText
              primary={
                <ExternalDashboardLink path={`/sl/b2b-policies/${policy.id}`}>
                  #{policy.id} · {policy.policyStatus}
                </ExternalDashboardLink>
              }
              secondary={policy.quoteID}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Stack spacing={1.5} sx={{ mt: hasUserAbove ? 1.5 : 0 }}>
      {hasUserAbove ? <Divider /> : null}
      <Box>
        <Typography variant="overline">
          <FormattedMessage defaultMessage="Companies" id="zendesk.sidebar.section.groups" />
        </Typography>
        <List dense disablePadding>
          {groupContexts.map(({ group, policies, policiesLoadFailed }) => (
            <ListItem key={group.id} disableGutters sx={{ alignItems: 'flex-start', flexDirection: 'column' }}>
              <ListItemText
                primary={
                  <ExternalDashboardLink path={`/sl/groups/${group.id}`}>
                    {group.name} (#{group.id})
                  </ExternalDashboardLink>
                }
                secondary={describeGroup(group)}
              />
              {renderPolicies(policies, policiesLoadFailed)}
            </ListItem>
          ))}
        </List>
      </Box>
    </Stack>
  );
};

export default GroupsSection;
